<template>
  <div class="page" v-if="can('category.show')">
    <h2 class="accent--text">
      <v-icon color="primary" class="ml-3" large>mdi-sitemap</v-icon>
      {{ category.name }}
      <v-icon color="primary" class="mx-1">mdi-chevron-left</v-icon>
      <span class="grey--text text--darken-1">التصنيفات</span>
      <back-button />
    </h2>
    <hr class="my-5 header-divider" />

    <!-- CREATE / UPDATE FROM -->
    <record-form @update="fetchRecords()" />

    <!-- NEW RECORD -->
    <div class="mb-3 mt-9 text-left">
      <v-btn
        v-if="can('category.store')"
        color="primary"
        elevation="0"
        @click="SET_RECORD_FORM({}); SET_EDIT_MODE(false); SHOW_RECORD_FORM()"
      >
        إضافة تصنيف رئيسي
        <v-icon class="mr-2">mdi-plus</v-icon>
      </v-btn>
    </div>

    <!-- TABLE -->
    <v-data-table
      show-expand
      single-expand
      item-key="id"
      class="elevation-1"
      :loading="table.loading"
      :headers="table.headers"
      :items="records"
      loading-text="جاري تحميل البيانات"
      no-data-text="لا يوجد بيانات"
      disable-filtering
      disable-pagination
      disable-sort
      hide-default-footer
    >
      <!-- CAPTION -->
      <template v-slot:item.caption="{ item }">
        <img
          :src="item.caption"
          width="50px"
          class="mt-3 mb-2 ml-2 rounded-lg"
        >
      </template>

      <!-- EXPAND ICON -->
      <template v-slot:item.data-table-expand="{ isExpanded, expand }">
        <v-btn
          v-if="isExpanded"
          small
          outlined
          color="deep-orange darken-2"
          class="mx-2"
          @click="expand(false)"
        >
          اخفاء
        </v-btn>
        <v-btn
          v-else
          small
          outlined
          color="cyan darken-3"
          class="mx-2"
          @click="expand(true)"
        >
          اظهار
        </v-btn>
      </template>

      <!-- EXPANDED ITEM -->
      <template v-slot:expanded-item="{ headers, item }">
        <td class="pa-0" :colspan="headers.length">
          <sub-sections-table class="pa-3 grey lighten-4" :section="item" />
        </td>
      </template>

      <!-- CONTROLS -->
      <template v-slot:item.controls="{ item }">
        <v-btn
          v-if="can('category.edit')"
          small
          icon
          color="success"
          class="mx-2"
          @click="SET_RECORD_FORM(item); SET_EDIT_MODE(true); SHOW_RECORD_FORM()"
        >
          <v-icon>mdi-circle-edit-outline</v-icon>
        </v-btn>

        <v-btn
          v-if="can('category.delete')"
          small
          icon
          color="error"
          class="mx-2"
          @click="remove(item)"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- PAGINATION -->
    <div class="text-center mt-7">
      <v-pagination
        total-visible="7"
        circle
        :value="parseInt(filters.page)"
        :length="totalPages"
        @input="$router.replace({ query: { ...$route.query, page: $event } })"
      ></v-pagination>
    </div>
  </div>
</template>


<script>
import RecordForm from './RecordForm';
import SubSectionsTable from './subSections/Table';
import { mapState, mapMutations } from 'vuex';

export default {
  name: 'sections',
  components: { SubSectionsTable, RecordForm },

  data: function () {
    return {
      table: {
        headers: [
          {
            value: 'id',
            text: 'مسلسل التصنيف الرئيسي'
          },
          {
            value: 'caption',
            text: 'الصورة'
          },
          {
            value: 'name',
            text: 'الاسم'
          },
          {
            value: 'sub_sections_count',
            text: 'عدد التصنيفات الفرعية'
          },
          {
            value: 'articles_count',
            text: 'عدد المواد'
          },
          {
            value: 'created_at',
            text: 'تاريخ الإضافة'
          },
          {
            value: 'data-table-expand',
            text: 'التصنيفات الفرعية',
            width: '135px'
          },
          {
            value: 'controls',
            text: 'اعدادات'
          },
        ],
        loading: false
      }
    }
  },


  computed: {
    ...mapState('sections', ['category', 'records', 'filters', 'totalPages']
    )
  },


  watch: {
    '$route': {
      async handler(route) {
        if (!route.query.page) {
          await this.$router.replace({ query: { ...route.query, page: 1 } });
          return;
        }

        const categoryId = route.params.id;
        await this.$store.dispatch('sections/fetchCategory', { id: categoryId });

        this.SET_FILTERS({ ...route.query, category_id: categoryId });
        this.fetchRecords();
      },
      deep: true,
      immediate: true,
    }
  },


  methods: {
    ...mapMutations(
      'sections',
      [
        'SHOW_RECORD_FORM',
        'SET_EDIT_MODE',
        'SET_RECORD_FORM',
        'SET_FILTERS'
      ]
    ),

    fetchRecords() {
      this.table.loading = true;

      this.$store.dispatch('sections/fetchRecords')
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر جلب البيانات, يرجي التأكد من حالة الشبكة',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });
    },

    async remove(selectedRecord) {
      const confirmMessageParams = [
        `حذف التصنيف الرئيسي رقم ${selectedRecord.id}!`,
        `هل أنت متأكد من حذف التصنيف الرئيسي '${selectedRecord.name}'؟`,
        { color: 'error' }
      ];
      if (!await this.$root.$confirm(...confirmMessageParams)) return;

      this.table.loading = true;

      this.$store.dispatch('sections/delete', { recordId: selectedRecord.id })
        .then(() => {
          this.$bus.$emit('showSnackbar', {
            text: 'تم حذف التصنيف الرئيسي بنجاح',
            color: 'success'
          });
        })
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر حذف التصنيف الرئيسي',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });;
    }
  }
}
</script>
